import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { ActivityType } from "@/types/activity"
import { SleepTable } from "./SleepTable"
import { NapTable } from "./NapTable"
import { FeedingTable } from "./FeedingTable"
import { Loader2 } from "lucide-react"
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination"

interface ActivityTablesProps {
  sleepActivities: ActivityType[]
  napActivities: ActivityType[]
  feedingActivities: ActivityType[]
  isLoading: boolean
  error: Error | null
  tabValue: 'all' | 'sleep' | 'nap' | 'feeding'
  onTabChange: (value: 'all' | 'sleep' | 'nap' | 'feeding') => void
  onActivityClick?: (activity: ActivityType) => void
  onEditActivity?: (activity: ActivityType, type: 'sleep' | 'nap' | 'feeding') => void
  onDeleteActivity?: (activity: ActivityType, type: 'sleep' | 'nap' | 'feeding') => void
  timezone?: string
  pageSize?: number
  currentPage?: number
  onPageChange?: (page: number) => void
}

/**
 * Component that displays activity tables in tabs, now connected to the filter state
 */
export function ActivityTables({
  sleepActivities,
  napActivities,
  feedingActivities,
  isLoading,
  error,
  tabValue,
  onTabChange,
  onActivityClick = () => {},
  onEditActivity = () => {},
  onDeleteActivity = () => {},
  timezone,
  pageSize,
  currentPage,
  onPageChange = () => {},
}: ActivityTablesProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-primary mr-2" />
        <span>Loading activities...</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="py-8 text-destructive">
        Error loading activities: {error.message}
      </div>
    )
  }

  // Handle all activities view
  const showAllActivities = tabValue === 'all'
  
  // Default to sleep tab if currently on 'all' for the tabs component
  // since Tabs component needs a specific tab value
  const activeTabValue = tabValue === 'all' ? 'sleep' : tabValue

  // Helper function to get the total number of activities for the current tab
  function getActivityCount() {
    switch(tabValue) {
      case 'sleep': return sleepActivities.length;
      case 'nap': return napActivities.length;
      case 'feeding': return feedingActivities.length;
      case 'all': 
        return sleepActivities.length + napActivities.length + feedingActivities.length;
      default: return 0;
    }
  }

  return (
    <div>
      {/* Show all activities if 'all' tab is selected */}
      {showAllActivities && (
        <div className="mb-6 space-y-8">
          <div>
            <h3 className="text-lg font-medium mb-3">Sleep ({sleepActivities.length})</h3>
            <SleepTable 
              activities={sleepActivities} 
              onActivityClick={onActivityClick}
              onEditActivity={(activity) => onEditActivity(activity, 'sleep')}
              onDeleteActivity={(activity) => onDeleteActivity(activity, 'sleep')}
              timezone={timezone}
            />
          </div>
          
          <div>
            <h3 className="text-lg font-medium mb-3">Nap ({napActivities.length})</h3>
            <NapTable 
              activities={napActivities} 
              onActivityClick={onActivityClick}
              onEditActivity={(activity) => onEditActivity(activity, 'nap')}
              onDeleteActivity={(activity) => onDeleteActivity(activity, 'nap')}
              timezone={timezone}
            />
          </div>
          
          <div>
            <h3 className="text-lg font-medium mb-3">Feeding ({feedingActivities.length})</h3>
            <FeedingTable 
              activities={feedingActivities} 
              onActivityClick={onActivityClick}
              onEditActivity={(activity) => onEditActivity(activity, 'feeding')}
              onDeleteActivity={(activity) => onDeleteActivity(activity, 'feeding')}
              timezone={timezone}
            />
          </div>
        </div>
      )}
      
      {/* Show individual tabs if a specific tab is selected */}
      {!showAllActivities && (
        <Tabs value={activeTabValue} onValueChange={onTabChange} className={showAllActivities ? 'hidden' : ''}>
          <TabsList className="w-full mb-6">
            <TabsTrigger value="sleep" className="flex-1">Sleep ({sleepActivities.length})</TabsTrigger>
            <TabsTrigger value="nap" className="flex-1">Nap ({napActivities.length})</TabsTrigger>
            <TabsTrigger value="feeding" className="flex-1">Feeding ({feedingActivities.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="sleep">
            <SleepTable 
              activities={sleepActivities} 
              onActivityClick={onActivityClick}
              onEditActivity={(activity) => onEditActivity(activity, 'sleep')}
              onDeleteActivity={(activity) => onDeleteActivity(activity, 'sleep')}
              timezone={timezone}
            />
          </TabsContent>

          <TabsContent value="nap">
            <NapTable 
              activities={napActivities} 
              onActivityClick={onActivityClick}
              onEditActivity={(activity) => onEditActivity(activity, 'nap')}
              onDeleteActivity={(activity) => onDeleteActivity(activity, 'nap')}
              timezone={timezone}
            />
          </TabsContent>

          <TabsContent value="feeding">
            <FeedingTable 
              activities={feedingActivities} 
              onActivityClick={onActivityClick}
              onEditActivity={(activity) => onEditActivity(activity, 'feeding')}
              onDeleteActivity={(activity) => onDeleteActivity(activity, 'feeding')}
              timezone={timezone}
            />
          </TabsContent>
        </Tabs>
      )}

      {/* Pagination controls */}
      {(sleepActivities.length > 0 || napActivities.length > 0 || feedingActivities.length > 0) && (
        <Pagination className="mt-6">
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious 
                href="#" 
                onClick={(e) => {
                  e.preventDefault();
                  if (currentPage && currentPage > 1) onPageChange(currentPage - 1);
                }}
                aria-disabled={currentPage === 1}
                className={currentPage === 1 ? "pointer-events-none opacity-50" : ""}
              />
            </PaginationItem>
            
            {currentPage && pageSize && (
              Array.from(
                { length: Math.min(5, Math.ceil(getActivityCount() / pageSize)) },
                (_, i) => {
                  // Display a range of pages centered around the current page
                  const totalPages = Math.ceil(getActivityCount() / pageSize);
                  let pageNum;
                  
                  if (totalPages <= 5) {
                    // If 5 or fewer pages, show all
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    // If near the start, show first 5 pages
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    // If near the end, show last 5 pages
                    pageNum = totalPages - 4 + i;
                  } else {
                    // Otherwise show 2 before and 2 after current page
                    pageNum = currentPage - 2 + i;
                  }
                  
                  return (
                    <PaginationItem key={pageNum}>
                      <PaginationLink 
                        href="#" 
                        onClick={(e) => {
                          e.preventDefault();
                          onPageChange(pageNum);
                        }}
                        isActive={currentPage === pageNum}
                      >
                        {pageNum}
                      </PaginationLink>
                    </PaginationItem>
                  );
                }
              )
            )}
            
            <PaginationItem>
              <PaginationNext 
                href="#" 
                onClick={(e) => {
                  e.preventDefault();
                  if (currentPage && pageSize) {
                    const totalPages = Math.ceil(getActivityCount() / pageSize);
                    if (currentPage < totalPages) onPageChange(currentPage + 1);
                  }
                }}
                aria-disabled={pageSize && currentPage && currentPage >= Math.ceil(getActivityCount() / pageSize)}
                className={pageSize && currentPage && currentPage >= Math.ceil(getActivityCount() / pageSize) ? "pointer-events-none opacity-50" : ""}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}
    </div>
  );
}
